import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Users, Search, Filter, Phone, X, Eye, Calendar, DollarSign, Info, Download, Wallet, Receipt } from "lucide-react";
import {
  subscriberAPI,
  operatorAPI,
  feeCollectionAPI,
  formatCurrency,
  securitfeeAPI,
  topupAPI,
} from "../services/api";
import * as XLSX from "xlsx";
import { formatStatusLabel } from "../utils/formatStatus";
import { DEFAULT_REPORT_MONTH, DEFAULT_REPORT_YEAR } from "../utils/reportPeriod";

const MONTHS = [
  { value: 1, label: "January" }, { value: 2, label: "February" }, { value: 3, label: "March" },
  { value: 4, label: "April" }, { value: 5, label: "May" }, { value: 6, label: "June" },
  { value: 7, label: "July" }, { value: 8, label: "August" }, { value: 9, label: "September" },
  { value: 10, label: "October" }, { value: 11, label: "November" }, { value: 12, label: "December" },
];
const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: 6 }, (_, i) => CURRENT_YEAR - i);

const ManagementSubscriber = () => {
  const { t } = useTranslation();
  const [subscribers, setSubscribers] = useState([]);
  const [operators, setOperators] = useState([]);
  const [statistics, setStatistics] = useState({});
  const [pagination, setPagination] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [filterOperator, setFilterOperator] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedSubscriber, setSelectedSubscriber] = useState(null);
  const [subscriber, setSubscriber] = useState({});
  const [securityFees, setSecurityFees] = useState([]);
  const [topUps, setTopUps] = useState([]);
  const [activeDetailTab, setActiveDetailTab] = useState("security");
  const [totalSubscribersDefault, setTotalSubscribersDefault] = useState([]);
  const [filterTotalSubscribers, setFilterTotalSubscribers] = useState({
    total: 0,
    active: 0,
    suspended: 0,
  });
  const [isCollected, setIsCollected] = useState('all');
  const [selectedMonth, setSelectedMonth] = useState(DEFAULT_REPORT_MONTH);
  const [selectedYear, setSelectedYear] = useState(DEFAULT_REPORT_YEAR);
  const [appliedMonth, setAppliedMonth] = useState(DEFAULT_REPORT_MONTH);
  const [appliedYear, setAppliedYear] = useState(DEFAULT_REPORT_YEAR);

  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);

  useEffect(() => {
    const fetchTotalSubscribers = async () => {
      const response = await subscriberAPI.getTotalSubscribers({ month: appliedMonth, year: appliedYear });
      setTotalSubscribersDefault(response.data || []);
    };
    fetchTotalSubscribers();
  }, [appliedMonth, appliedYear]);

  useEffect(() => {
    if (filterOperator === "all") {
      const total = totalSubscribersDefault?.reduce(
        (sum, subscriber) => sum + subscriber.total_subscribers,
        0,
      );
      const active = totalSubscribersDefault?.reduce(
        (sum, subscriber) => sum + subscriber.active_subscribers,
        0,
      );
      const suspended = total - active;
      console.log({ total, active, suspended });
      setFilterTotalSubscribers({
        total: total,
        active: active,
        suspended: suspended,
      });
    } else {
      const total = totalSubscribersDefault
        ?.filter(
          (subscriber) => subscriber.operator_id === parseInt(filterOperator),
        )
        .reduce((sum, subscriber) => sum + subscriber.total_subscribers, 0);
      const active = totalSubscribersDefault
        ?.filter(
          (subscriber) => subscriber.operator_id === parseInt(filterOperator),
        )
        .reduce((sum, subscriber) => sum + subscriber.active_subscribers, 0);
      const suspended = total - active;
      console.log({ total, active, suspended });
      setFilterTotalSubscribers({
        total: total,
        active: active,
        suspended: suspended,
      });
    }
  }, [filterOperator, totalSubscribersDefault]);

  // Fetch subscribers data
  const fetchSubscribers = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        page: currentPage,
        limit: 50,
        search: searchTerm,
        operator: filterOperator,
        status: filterStatus,
        isCollected: isCollected,
        month: appliedMonth,
        year: appliedYear,
      };

      const response = await subscriberAPI.getAll(params);
      if (response?.data) {
        setSubscribers(response.data.subscribers || []);
        setStatistics(response.data.statistics || {});
        setPagination(response.data.pagination || {});
      }
    } catch (error) {
      console.error("Error fetching subscribers:", error);
      setError(t('managementSubscriber.loadError'));
      setSubscribers([]);
      setStatistics({});
      setPagination({});
    } finally {
      setLoading(false);
    }
  };

  // Fetch operators for filter dropdown
  const fetchOperators = async () => {
    try {
      const response = await operatorAPI.getAll();
      setOperators(response.data || []);
    } catch (error) {
      console.error("Error fetching operators:", error);
    }
  };

  useEffect(() => {
    fetchOperators();
  }, []);

  useEffect(() => {
    fetchSubscribers();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetch when pagination/filters change
  }, [currentPage, searchTerm, filterOperator, filterStatus, isCollected, appliedMonth, appliedYear]);

  // Trigger search by MSISDN (called on button click or Enter)
  const handleSearchClick = () => {
    setSearchTerm(searchInput.trim());
    setCurrentPage(1);
  };

  // Clear MSISDN search and show all
  const handleClearSearch = () => {
    setSearchInput("");
    setSearchTerm("");
    setCurrentPage(1);
  };

  const handleOperatorFilter = (value) => {
    setFilterOperator(value);
    setCurrentPage(1);
  };

  const handlePeriodSearch = () => {
    setAppliedMonth(selectedMonth);
    setAppliedYear(selectedYear);
    setCurrentPage(1);
  };

  const handleStatusFilter = (value) => {
    setFilterStatus(value);
    setCurrentPage(1);
  };

  // Placeholder until the API exposes a pre-resale balance
  const getLastBalanceBeforeResale = (sub) => {
    if ((sub?.status || "").toLowerCase() === "active") return null;
    const real =
      sub?.last_balance_before_resale ??
      sub?.lastBalanceBeforeResale ??
      sub?.balance_before_resale ??
      sub?.balance_before;
    if (real != null && real !== "") return Number(real);
    const balance = Number(sub?.balance ?? 0);
    if (!Number.isFinite(balance)) return null;
    const seed = Number(String(sub?.msisdn ?? "").slice(-3)) || 137;
    return balance + (seed % 9 + 1) * 5000;
  };

  const getSecurityFeeTotal = (fees = []) =>
    fees.reduce((sum, tx) => sum + Number(tx.amount ?? tx.fee_amount ?? tx.feeAmount ?? tx.amount_kip ?? 0), 0);

  const getTopUpTotal = (rows = []) =>
    rows.reduce((sum, tx) => sum + Number(tx.recharge_amt ?? tx.amount ?? 0), 0);

  const getTransactionRef = (tx) => {
    const ref = tx?.uid;
    return ref != null && ref !== "" ? String(ref) : "—";
  };

  const handleViewDetail = async (sub) => {
    // console.log({subscriber});
    setSelectedSubscriber(sub);
    setShowDetailModal(true);
    setSubscriber({});
    setActiveDetailTab("security");
    setTopUps([]);
    setDetailError(null);
    setDetailLoading(true);
    try {
      const subscriber_id = sub?.subscriber_id ?? "";
      const msisdn = sub?.msisdn ?? "";
      const [subscriberRes, securityFeesRes, topUpsRes] = await Promise.all([
        subscriberAPI.getById(subscriber_id, { month: appliedMonth, year: appliedYear }),
        securitfeeAPI.getSecurityFeeTransactions(msisdn),
        topupAPI.getByMsisdn(msisdn),
      ]);
      setSecurityFees(securityFeesRes?.data ?? []);
      setTopUps(topUpsRes?.data ?? []);
      setSubscriber(subscriberRes?.data?.subscriber ?? {});
    } catch (err) {
      console.error("Error fetching subscriber transactions:", err);
      setDetailError(
        err?.message || t('managementSubscriber.txnLoadError'),
      );
      setSubscriber({});
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetailModal = () => {
    setShowDetailModal(false);
    setSelectedSubscriber(null);
    setSubscriber({});
    setSecurityFees([]);
    setTopUps([]);
    setActiveDetailTab("security");

    setDetailError(null);
  };

  // Status display: dot + label with consistent styling (case-insensitive)
  const getStatusDisplay = (status) => {
    const s = (status || "").toLowerCase();
    if (s === "completed" || s === "success" || s === "collected" || s === "paid")
      return {
        label: s === "success" ? t('common.statusDisplayLabels.success') : s === "collected" ? t('common.statusDisplayLabels.collected') : s === "paid" ? t('common.statusDisplayLabels.paid') : t('common.statusDisplayLabels.completed'),
        dot: "bg-emerald-500",
        bg: "bg-emerald-50",
        text: "text-emerald-800",
        border: "border-emerald-200",
      };
    if (s === "active")
      return {
        label: t('common.statusDisplayLabels.active'),
        dot: "bg-emerald-500",
        bg: "bg-emerald-50",
        text: "text-emerald-800",
        border: "border-emerald-200",
      };
    if (s === "suspended" || s === "failed" || s === "cancelled" || s === "inactive")
      return {
        label: s === "failed" ? t('common.statusDisplayLabels.failed') : s === "cancelled" ? t('common.statusDisplayLabels.cancelled') : s === "inactive" ? t('common.statusDisplayLabels.inactive') : t('common.statusDisplayLabels.suspended'),
        dot: "bg-red-500",
        bg: "bg-red-50",
        text: "text-red-800",
        border: "border-red-200",
      };
    if (s === "pending")
      return {
        label: t('common.statusDisplayLabels.pending'),
        dot: "bg-amber-500",
        bg: "bg-amber-50",
        text: "text-amber-800",
        border: "border-amber-200",
      };
    return {
      label: t(`common.statusDisplayLabels.${(status || '').toLowerCase()}`, { defaultValue: formatStatusLabel(status || "UNKNOWN") }),
      dot: "bg-gray-400",
      bg: "bg-gray-50",
      text: "text-gray-700",
      border: "border-gray-200",
    };
  };

  // Small inline sparkline component (SVG) for recent activity
  const Sparkline = ({ data = [], width = 160, height = 40, stroke = '#3b82f6' }) => {
    const nums = (data || []).map((v) => {
      const n = Number(v);
      return Number.isNaN(n) ? 0 : n;
    });
    if (!nums.length) return <div className="text-xs text-gray-400">{t('managementSubscriber.noActivity')}</div>;
    if (nums.length === 1) {
      return (
        <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} className="block">
          <line x1="0" y1={height - 2} x2={width} y2={height - 2} stroke="#eef2ff" strokeWidth="2" />
          <circle cx={width / 2} cy={height / 2} r={3} fill={stroke} />
        </svg>
      );
    }
    const max = Math.max(...nums);
    const min = Math.min(...nums);
    const range = max - min || 1;
    const step = width / (nums.length - 1);
    const points = nums
      .map((v, i) => {
        const x = Number((i * step).toFixed(2));
        const y = Number((height - ((v - min) / range) * height).toFixed(2));
        return `${x},${y}`;
      })
      .join(' ');
    return (
      <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} className="block">
        <polyline fill="none" stroke={stroke} strokeWidth="2" points={points} strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  };

  const formatBalance = (value) => {
    if (value == null || value === "") return "—";
    const n = Number(value);
    if (Number.isNaN(n)) return String(value);
    return n.toLocaleString(undefined);
  };

  const formatDate = (value) => {
    if (!value) return "—";
    try {
      const d = new Date(value);
      return Number.isNaN(d.getTime())
        ? "—"
        : d.toLocaleDateString(undefined, {
            year: "numeric",
            month: "short",
            day: "numeric",
          });
    } catch {
      return "—";
    }
  };

  /** event_month is a calendar month (YYYY-MM-DD), not a local timestamp */
  const formatEventMonth = (tx) => {
    if (tx?.event_month_year && tx?.event_month_num) {
      const d = new Date(Date.UTC(tx.event_month_year, tx.event_month_num - 1, 1));
      return d.toLocaleDateString(undefined, { year: "numeric", month: "long", timeZone: "UTC" });
    }
    const str = String(tx?.event_month ?? tx?.data_date ?? "");
    const match = str.match(/(\d{4})-(\d{2})/);
    if (match) {
      const d = new Date(Date.UTC(parseInt(match[1], 10), parseInt(match[2], 10) - 1, 1));
      return d.toLocaleDateString(undefined, { year: "numeric", month: "long", timeZone: "UTC" });
    }
    return formatDate(tx?.event_month ?? tx?.data_date);
  };

  const formatDateTime = (value) => {
    if (!value) return "—";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  };

  const downloadXlsx = (filename, sheetName, rows) => {
    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName.slice(0, 31));
    XLSX.writeFile(workbook, filename.endsWith(".xlsx") ? filename : `${filename}.xlsx`);
  };

  const handleExportTabExcel = () => {
    const msisdn = selectedSubscriber?.msisdn ?? "subscriber";
    if (activeDetailTab === "security") {
      const rows = securityFees.map((tx) => ({
        Date: formatDateTime(tx.date ?? tx.collection_date ?? ""),
        "Transaction Ref": getTransactionRef(tx),
        Period: String(tx.period ?? tx.billing_period ?? tx.month ?? tx.year ?? ""),
        Amount: Number(tx.amount ?? tx.fee_amount ?? tx.feeAmount ?? tx.amount_kip ?? 0),
        Status: formatStatusLabel(tx.status ?? tx.transaction_status ?? ""),
      }));
      downloadXlsx(`security-fee-${msisdn}`, "Security fee", rows);
      return;
    }
    const rows = topUps.map((tx) => ({
      "Event Time": formatDateTime(tx.event_time ?? tx.timestamp),
      "Event Month": formatEventMonth(tx),
      Operator: tx.operator_name ?? tx.operator ?? "",
      MSISDN: tx.isdn ?? tx.msisdn ?? "",
      "Recharge Amount": Number(tx.recharge_amt ?? tx.amount ?? 0),
      "Current Balance": Number(tx.cur_balance ?? tx.new_balance ?? 0),
      "Payment Type": tx.payment_type ?? tx.channel ?? "",
      "Source File": tx.source_file ?? "",
    }));
    downloadXlsx(`topup-${msisdn}`, "Top-up", rows);
  };

  return (
    <main className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center">
            <Users className="h-8 w-8 mr-3 text-blue-600" />
            {t('managementSubscriber.title')}
          </h1>
          <p className="text-gray-600 mt-2">
            {t('managementSubscriber.subtitle')}
          </p>
          <p className="text-sm text-gray-500 mt-1">
            {t('common.showingDataFor')}: <strong>{t(`common.monthNames.${appliedMonth}`, { defaultValue: appliedMonth })} {appliedYear}</strong>
          </p>
        </div>

        {/* Period filter */}
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <div className="flex flex-wrap items-center gap-3">
            <Calendar className="h-5 w-5 text-gray-400" />
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(Number(e.target.value))}
              className="border border-gray-300 rounded-md px-3 py-2 bg-white focus:outline-none focus:ring-1 focus:ring-primary-500"
            >
              {MONTHS.map((m) => (
                <option key={m.value} value={m.value}>{t(`common.monthNames.${m.value}`)}</option>
              ))}
            </select>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(Number(e.target.value))}
              className="border border-gray-300 rounded-md px-3 py-2 bg-white focus:outline-none focus:ring-1 focus:ring-primary-500"
            >
              {YEAR_OPTIONS.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
            <button
              type="button"
              onClick={handlePeriodSearch}
              className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
            >
              <Search className="h-4 w-4 mr-2" />
              {t('common.applyPeriod')}
            </button>
          </div>
        </div>

        {/* Filters and Search */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0 md:space-x-4">
            {/* Search */}
            <div className="relative flex-1 max-w-md flex gap-2">
              <div className="relative flex-1">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder={t('common.searchMsisdnPlaceholder')}
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSearchClick()}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <button
                type="button"
                onClick={handleSearchClick}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 whitespace-nowrap"
              >
                <Search className="h-5 w-5 mr-2" />
                {t('common.search')}
              </button>
              {searchTerm && (
                <button
                  type="button"
                  onClick={handleClearSearch}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 whitespace-nowrap"
                >
                  <X className="h-5 w-5 mr-2" />
                  {t('common.clear')}
                </button>
              )}
            </div>

            {/* Filters */}
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <Filter className="h-5 w-5 text-gray-400" />
                <select
                  value={filterOperator}
                  onChange={(e) => handleOperatorFilter(e.target.value)}
                  className="border border-gray-300 rounded-md px-3 py-2 bg-white focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="all">{t('common.allOperators')}</option>
                  {operators.map((operator) => (
                    <option
                      key={operator.operator_id}
                      value={operator.operator_id}
                    >
                      {operator.operator_name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-center space-x-2">
                <select
                  value={isCollected}
                  onChange={(e) => setIsCollected(e.target.value)}
                  className="border border-gray-300 rounded-md px-3 py-2 bg-white focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="all">{t('common.all')}</option>
                  <option value="true">{t('common.collected')}</option>
                  <option value="false">{t('common.notCollected')}</option>
                </select>
              </div>
              <div className="flex items-center space-x-2">
                <select
                  value={filterStatus}
                  onChange={(e) => handleStatusFilter(e.target.value)}
                  className="border border-gray-300 rounded-md px-3 py-2 bg-white focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="all">{t('common.allStatus')}</option>
                  <option value="ACTIVE">{t('common.active')}</option>
                  <option value="SUSPEND">{t('common.suspended')}</option>
                  <option value="DEACTIVE">{t('common.deactive')}</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-red-400"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-700">{error}</p>
              </div>
              <div className="ml-auto pl-3">
                <button
                  onClick={() => {
                    setError(null);
                    fetchSubscribers();
                  }}
                  className="text-red-700 hover:text-red-900 text-sm underline"
                >
                  {t('common.retry')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Users className="h-6 w-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">
                  {t('common.totalSubscribers')}
                </p>
                <p className="text-2xl font-bold text-gray-900">
                  {filterTotalSubscribers.total.toLocaleString() || "0"}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <Users className="h-6 w-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">{t('common.active')}</p>
                <p className="text-2xl font-bold text-gray-900">
                  {filterTotalSubscribers.active.toLocaleString() || "0"}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Users className="h-6 w-6 text-yellow-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">{t('common.suspended')}</p>
                <p className="text-2xl font-bold text-gray-900">
                  {filterTotalSubscribers.suspended.toLocaleString() || "0"}
                </p>
              </div>
            </div>
          </div>

          {/* <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Users className="h-6 w-6 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">New This Month</p>
              <p className="text-2xl font-bold text-gray-900">{statistics.new_this_month?.toLocaleString() || '0'}</p>
            </div>
          </div>
        </div> */}
        </div>

        {/* Subscribers Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">
              {t('managementSubscriber.list')}
            </h3>
          </div>

          {loading ? (
            <div className="p-6 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
              <p className="mt-2 text-gray-600">{t('managementSubscriber.loading')}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('managementSubscriber.colSubscriber')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('common.operator')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('managementSubscriber.colServiceType')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('common.status')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('managementSubscriber.colBalance')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('managementSubscriber.colLastCharge')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('managementSubscriber.colLastActivity')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('common.actions')}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {subscribers.map((subscriber) => (
                    <tr
                      key={subscriber.subscriber_id}
                      className="hover:bg-gray-50"
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                              <Phone className="h-5 w-5 text-primary-600" />
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {subscriber.msisdn}
                            </div>
                            <div className="text-sm text-gray-500 flex items-center">
                              <Phone className="h-4 w-4 mr-1" />
                              {subscriber.email || t('managementSubscriber.noPhone')}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {
                            operators.find(
                              (o) => o.operator_id === subscriber.operator_id,
                            )?.operator_name
                          }
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {subscriber.serviceType || subscriber.subscriber_type || "N/A"}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {(() => {
                          const s = getStatusDisplay(subscriber.status);
                          return (
                            <span
                              className={`inline-flex items-center gap-2 px-2.5 py-1 rounded-md border text-xs font-medium ${s.bg} ${s.text} ${s.border}`}
                            >
                              <span
                                className={`h-2 w-2 rounded-full shrink-0 ${s.dot}`}
                                aria-hidden
                              />
                              {s.label}
                            </span>
                          );
                        })()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 tabular-nums">
                        {formatBalance(subscriber.balance)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(
                          subscriber.lastchargedate ??
                            subscriber.lastChargeDate,
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {(() => {
                          const lastActivity =
                            subscriber.lastActivity ??
                            subscriber.last_activity_date ??
                            subscriber.last_status_date;
                          return lastActivity ? formatDate(lastActivity) : "—";
                        })()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          type="button"
                          onClick={() => handleViewDetail(subscriber)}
                          className="inline-flex items-center gap-1.5 text-primary-600 hover:text-primary-800 font-medium"
                        >
                          <Eye className="h-4 w-4" />
                          {t('managementSubscriber.viewDetail')}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {subscribers.length === 0 && !loading && (
            <div className="p-6 text-center text-gray-500">
              No subscribers found matching your criteria.
            </div>
          )}
        </div>

        {/* Pagination */}
        {pagination.total_pages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6 rounded-b-lg">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setCurrentPage(Math.max(currentPage - 1, 1))}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                {t('common.previous')}
              </button>
              <button
                onClick={() =>
                  setCurrentPage(
                    Math.min(currentPage + 1, pagination.total_pages),
                  )
                }
                disabled={currentPage === pagination.total_pages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                {t('common.next')}
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  {t('managementSubscriber.showingResults', {
                    from: (currentPage - 1) * pagination.per_page + 1,
                    to: Math.min(currentPage * pagination.per_page, pagination.total),
                    total: pagination.total,
                  })}
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button
                    onClick={() => setCurrentPage(Math.max(currentPage - 1, 1))}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    {t('common.previous')}
                  </button>
                  {Array.from(
                    { length: Math.min(5, pagination.total_pages) },
                    (_, i) => {
                      const page = i + Math.max(1, currentPage - 2);
                      if (page > pagination.total_pages) return null;
                      return (
                        <button
                          key={page}
                          onClick={() => setCurrentPage(page)}
                          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                            page === currentPage
                              ? "z-10 bg-primary-50 border-primary-500 text-primary-600"
                              : "bg-white border-gray-300 text-gray-500 hover:bg-gray-50"
                          }`}
                        >
                          {page}
                        </button>
                      );
                    },
                  )}
                  <button
                    onClick={() =>
                      setCurrentPage(
                        Math.min(currentPage + 1, pagination.total_pages),
                      )
                    }
                    disabled={currentPage === pagination.total_pages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    {t('common.next')}
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}

        {/* Security fee transactions detail modal */}
        {showDetailModal && selectedSubscriber && (
          <div
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={closeDetailModal}
            role="dialog"
            aria-modal="true"
            aria-labelledby="detail-modal-title"
          >
            <div
              className="bg-white rounded-xl shadow-xl max-w-[95vw] w-full h-[92vh] max-h-[92vh] overflow-hidden flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between shrink-0">
                <h2
                  id="detail-modal-title"
                  className="text-lg font-semibold text-gray-900"
                >
                  {t('managementSubscriber.detailTitle', { msisdn: selectedSubscriber.msisdn })}
                </h2>
                <button
                  type="button"
                  onClick={closeDetailModal}
                  className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                  aria-label="Close"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              {/* <pre>{JSON.stringify(selectedSubscriber, null, 2)}</pre> */}
              <div className="px-6 py-3 border-b border-gray-100 bg-gray-50/50 text-sm text-gray-600 shrink-0">
                <div className="flex flex-wrap items-center gap-4">
                  {selectedSubscriber.operator_id && (
                    <div className="text-sm">
                      <span className="text-gray-500">{t('managementSubscriber.operatorLabel')}</span>{" "}
                      <span className="font-medium text-gray-700">{
                        operators.find((o) => o.operator_id === selectedSubscriber.operator_id)?.operator_name
                      }</span>
                    </div>
                  )}
                  {(selectedSubscriber.serviceType || selectedSubscriber.subscriber_type) && (
                    <div className="text-sm">
                      <span className="text-gray-500">{t('managementSubscriber.serviceLabel')}</span>{" "}
                      <span className="font-medium text-gray-700">{selectedSubscriber.serviceType || selectedSubscriber.subscriber_type}</span>
                    </div>
                  )}
                  {selectedSubscriber.status && (
                    <div className="text-sm">
                      <span className="text-gray-500">{t('managementSubscriber.statusLabel')}</span>{" "}
                      <span className="font-medium text-gray-700">{t(`common.statusDisplayLabels.${String(selectedSubscriber.status || '').toLowerCase()}`, { defaultValue: formatStatusLabel(selectedSubscriber.status) })}</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-6">
                {detailLoading && (
                  <div className="flex flex-col items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-10 w-10 border-2 border-primary-500 border-t-transparent" />
                    <p className="mt-3 text-gray-600">{t('managementSubscriber.loadingTransactions')}</p>
                  </div>
                )}

                {detailError && !detailLoading && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800 text-sm">
                    {detailError}
                  </div>
                )}

                {!detailLoading && !detailError && (!subscriber || Object.keys(subscriber).length === 0) && (
                  <p className="text-gray-500 text-center py-8">{t('managementSubscriber.noDetails')}</p>
                )}
                {!detailLoading && !detailError && subscriber && Object.keys(subscriber).length > 0 && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="md:col-span-2 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      <div className="bg-white border rounded-lg p-4 shadow-sm flex items-center gap-4">
                        <div className="p-2 bg-primary-50 rounded-md">
                          <Phone className="h-6 w-6 text-primary-600" />
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">{t('common.msisdn')}</p>
                          <p className="text-lg font-semibold text-gray-900">{subscriber.msisdn || '—'}</p>
                        </div>
                      </div>

                      <div className="bg-white border rounded-lg p-4 shadow-sm flex items-center gap-4">
                        <div className="p-2 bg-green-50 rounded-md">
                          <DollarSign className="h-6 w-6 text-green-600" />
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">{t('managementSubscriber.balance')}</p>
                          <p className="text-lg font-semibold text-gray-900">{formatBalance(subscriber.balance)}</p>
                        </div>
                      </div>

                      <div className="bg-white border rounded-lg p-4 shadow-sm flex items-center gap-4">
                        <div className="p-2 bg-violet-50 rounded-md">
                          <Wallet className="h-6 w-6 text-violet-600" />
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">{t('managementSubscriber.lastBalanceBeforeResale')}</p>
                          <p className="text-lg font-semibold text-gray-900">{formatBalance(getLastBalanceBeforeResale(subscriber))}</p>
                        </div>
                      </div>

                      <div className="bg-white border rounded-lg p-4 shadow-sm flex items-center gap-4">
                        <div className="p-2 bg-amber-50 rounded-md">
                          <Calendar className="h-6 w-6 text-amber-600" />
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">{t('managementSubscriber.lastCharge')}</p>
                          <p className="text-lg font-semibold text-gray-900">{formatDate(subscriber.last_charge_date ?? subscriber.lastChargeDate)}</p>
                        </div>
                      </div>

                      <div className="bg-white border rounded-lg p-4 shadow-sm flex items-center gap-4">
                        <div className="p-2 bg-indigo-50 rounded-md">
                          <Receipt className="h-6 w-6 text-indigo-600" />
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">{t('managementSubscriber.fee')}</p>
                          <p className="text-lg font-semibold text-gray-900">{formatCurrency(getSecurityFeeTotal(securityFees))}</p>
                          <p className="text-xs text-gray-500 mt-1">{t('managementSubscriber.topup')}</p>
                          <p className="text-lg font-semibold text-gray-900">{formatCurrency(getTopUpTotal(topUps))}</p>
                        </div>
                      </div>

                      <div className="bg-white border rounded-lg p-4 shadow-sm flex flex-col justify-between gap-3">
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-sky-50 rounded-md">
                            <Info className="h-6 w-6 text-sky-600" />
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">{t('common.status')}</p>
                            {(() => {
                              const s = getStatusDisplay(subscriber.status);
                              return (
                                <span className={`inline-flex items-center px-3 py-1 rounded-md text-sm font-semibold ${s.bg} ${s.text} ${s.border}`}>
                                  <span className={`h-2 w-2 rounded-full mr-2 ${s.dot}`} />
                                  {s.label}
                                </span>
                              );
                            })()}
                          </div>
                        </div>
                        <div className="mt-2">
                          {(() => {
                            const sparkData = securityFees
                              .slice()
                              .slice(-12)
                              .map((tx) => Number(tx.amount ?? tx.fee_amount ?? tx.feeAmount ?? tx.amount_kip ?? 0));
                            return <Sparkline data={sparkData} width={140} height={36} stroke="#06b6d4" />;
                          })()}
                        </div>
                      </div>
                    </div>

                    <div className="md:col-span-1 bg-white border rounded-lg p-4 shadow-sm">
                      <h4 className="text-sm font-medium text-gray-700 mb-2">{t('managementSubscriber.subscriberDetails')}</h4>
                      <dl className="grid grid-cols-1 gap-y-2 text-sm">
                        {[
                          { key: 'imsi', label: t('common.imsi') },
                          { key: 'subscriber_type', label: t('managementSubscriber.subscriberType') },
                          { key: 'registration_date', label: t('managementSubscriber.registrationDate'), format: 'date' },
                          { key: 'status_detail', label: t('managementSubscriber.statusDetail') },
                          { key: 'charge_fee', label: t('managementSubscriber.chargeFee'), format: 'balance' },
                          { key: 'last_activity_date', label: t('managementSubscriber.lastActivityDate'), format: 'date' },
                        ].map(({ key, label, format }) => {
                          const raw = subscriber[key];
                          const value = raw == null || raw === '' ? '—' : format === 'date' ? formatDate(raw) : format === 'balance' ? formatBalance(raw) : String(raw);
                          return (
                            <div key={key} className="flex justify-between">
                              <dt className="text-gray-500">{label}</dt>
                              <dd className="text-gray-900">{value}</dd>
                            </div>
                          );
                        })}
                      </dl>
                    </div>
                  </div>
                )}

                {/* Transaction tabs */}
                <div className="mt-6">
                  <div className="border-b border-gray-200 flex items-end justify-between gap-4">
                    <nav className="-mb-px flex gap-6" role="tablist" aria-label="Transaction type">
                      {[
                        { id: 'security', label: t('managementSubscriber.tabSecurityFee'), count: securityFees.length },
                        { id: 'topup', label: t('managementSubscriber.topup'), count: topUps.length },
                      ].map(({ id, label, count }) => (
                        <button
                          key={id}
                          type="button"
                          role="tab"
                          aria-selected={activeDetailTab === id}
                          onClick={() => setActiveDetailTab(id)}
                          className={`whitespace-nowrap border-b-2 px-1 py-3 text-sm font-medium transition-colors ${
                            activeDetailTab === id
                              ? 'border-primary-500 text-primary-600'
                              : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                          }`}
                        >
                          {label}
                          <span className="ml-2 rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">{count}</span>
                        </button>
                      ))}
                    </nav>
                    <button
                      type="button"
                      onClick={handleExportTabExcel}
                      disabled={
                        activeDetailTab === 'security'
                          ? securityFees.length === 0 || detailLoading || !!detailError
                          : topUps.length === 0
                      }
                      className="mb-2 inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Download className="h-4 w-4" />
                      {t('common.exportExcel')}
                    </button>
                  </div>

                  {activeDetailTab === 'security' && !detailLoading && !detailError && (
                    <div className="mt-4">
                      {securityFees.length === 0 ? (
                        <p className="text-gray-500 text-center py-8">{t('managementSubscriber.noSecurityTxns')}</p>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="min-w-full divide-y divide-gray-200 text-sm">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('common.date')}</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('managementSubscriber.colTransactionRef')}</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('managementSubscriber.colPeriod')}</th>
                                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{t('managementSubscriber.colAmount')}</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('common.status')}</th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {securityFees.map((tx, index) => {
                                const date = tx.date ?? tx.collection_date ?? '—';
                                const period = tx.period ?? tx.billing_period ?? tx.month ?? tx.year ?? '—';
                                const amount = tx.amount ?? tx.fee_amount ?? tx.feeAmount ?? tx.amount_kip ?? 0;
                                const status = tx.status ?? tx.transaction_status ?? '—';
                                return (
                                  <tr key={tx.id ?? tx.uid ?? tx.transaction_id ?? index} className="hover:bg-gray-50">
                                    <td className="px-4 py-3 whitespace-nowrap text-gray-900">{formatDateTime(date)}</td>
                                    <td className="px-4 py-3 whitespace-nowrap text-gray-900">{getTransactionRef(tx)}</td>
                                    <td className="px-4 py-3 whitespace-nowrap text-gray-600">{String(period)}</td>
                                    <td className="px-4 py-3 whitespace-nowrap text-right tabular-nums text-gray-900">{formatCurrency(amount)}</td>
                                    <td className="px-4 py-3 whitespace-nowrap">
                                      {status === '—' ? (
                                        <span className="text-gray-500">—</span>
                                      ) : (() => {
                                        const ts = getStatusDisplay(status);
                                        return (
                                          <span className={`inline-flex items-center gap-2 px-2 py-0.5 rounded-md border text-xs font-medium ${ts.bg} ${ts.text} ${ts.border}`}>
                                            <span className={`h-2 w-2 rounded-full shrink-0 ${ts.dot}`} />
                                            {ts.label}
                                          </span>
                                        );
                                      })()}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}

                  {activeDetailTab === 'topup' && (
                    <div className="mt-4">
                      {topUps.length === 0 ? (
                        <p className="text-gray-500 text-center py-8">{t('managementSubscriber.noTopupTxns')}</p>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="min-w-full divide-y divide-gray-200 text-sm">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('managementSubscriber.colEventTime')}</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('managementSubscriber.colEventMonth')}</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('common.operator')}</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('common.msisdn')}</th>
                                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{t('managementSubscriber.colRechargeAmount')}</th>
                                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{t('managementSubscriber.colCurrentBalance')}</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('managementSubscriber.colPaymentType')}</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('managementSubscriber.colSourceFile')}</th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {topUps.map((tx, index) => (
                                  <tr key={`${tx.event_time ?? tx.timestamp ?? index}-${tx.source_file ?? index}`} className="hover:bg-gray-50">
                                    <td className="px-4 py-3 whitespace-nowrap text-gray-900">{formatDateTime(tx.event_time ?? tx.timestamp)}</td>
                                    <td className="px-4 py-3 whitespace-nowrap text-gray-900">{formatEventMonth(tx)}</td>
                                    <td className="px-4 py-3 whitespace-nowrap text-gray-600">{tx.operator_name ?? tx.operator ?? "—"}</td>
                                    <td className="px-4 py-3 whitespace-nowrap text-gray-900">{tx.isdn ?? tx.msisdn ?? "—"}</td>
                                    <td className="px-4 py-3 whitespace-nowrap text-right tabular-nums text-gray-900">{formatCurrency(tx.recharge_amt ?? tx.amount)}</td>
                                    <td className="px-4 py-3 whitespace-nowrap text-right tabular-nums text-gray-600">{formatCurrency(tx.cur_balance ?? tx.new_balance)}</td>
                                    <td className="px-4 py-3 whitespace-nowrap text-gray-600">{tx.payment_type ?? tx.channel ?? "—"}</td>
                                    <td className="px-4 py-3 text-gray-600 max-w-xs truncate" title={tx.source_file ?? ""}>{tx.source_file ?? "—"}</td>
                                  </tr>
                                ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </main>
  );
};

export default ManagementSubscriber;
